/**
 * Align a Shopify event with the ledger row it produced.
 *
 * Two clocks are involved and neither is authoritative for the other: the
 * ledger stamps when WE received the webhook, Shopify stamps when the action
 * happened there. Measured against prod on 2026-08-20 the gap was 1.7s for a
 * publish and 2.7s for a create — small, but never zero, so an exact-match
 * join would attribute nothing.
 *
 * A window rather than a nearest-neighbour pick, deliberately: publishing one
 * product emitted NINE `published` events plus a `status_changed` inside two
 * seconds, and every one of them belongs to the row that followed. Nearest
 * neighbour would name one and drop eight, hiding the fact that a burst of
 * sales-channel publishes is one operator action.
 */
import type { ShopifyActorEvent } from '@/services';

/**
 * How far apart the two clocks may be and still describe the same action.
 *
 * Sixty seconds: two orders of magnitude above the observed skew, and still
 * far below the gap between distinct operator actions on one record (the
 * closest pair observed in prod was ~18 hours apart). Widening this trades
 * a missed attribution for a wrong one, which is the worse failure — a named
 * person who did not do it.
 */
export const SHOPIFY_MATCH_WINDOW_MS = 60_000;

/** The ledger row fields the match needs — deliberately not the whole entry. */
export interface MatchableLedgerRow {
  id: string;
  createdAt: string;
}

function epochOrNull(timestamp: string): number | null {
  const ms = new Date(timestamp).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Map each ledger row to the Shopify events that plausibly caused it, one
 * entry per distinct actor.
 *
 * Rows with no match are ABSENT from the map rather than present with an
 * empty list: "Shopify does not know who did this" is the normal outcome for
 * a field edit, and the caller renders nothing for it.
 *
 * Only what a HUMAN did survives. Two filters, both needed: an event with no
 * `actor` names nobody, and an event with `by_user !== true` was the platform
 * or an app — `actor` reads "Shopify" for those, which answers the operator's
 * question with noise. Matching on that literal string would be a magic-string
 * stand-in for the flag Shopify already gives us.
 */
export function matchShopifyActors(
  rows: readonly MatchableLedgerRow[],
  events: readonly ShopifyActorEvent[],
): Map<string, ShopifyActorEvent[]> {
  const dated = events
    .filter((event) => event.actor !== null && event.actor !== '' && event.by_user === true)
    .map((event) => ({ event, at: epochOrNull(event.occurred_at) }))
    .filter((entry): entry is { event: ShopifyActorEvent; at: number } => entry.at !== null);

  const matched = new Map<string, ShopifyActorEvent[]>();
  for (const row of rows) {
    const rowAt = epochOrNull(row.createdAt);
    if (rowAt === null) continue;

    const byActor = new Map<string, ShopifyActorEvent>();
    for (const { event, at } of dated) {
      if (Math.abs(at - rowAt) > SHOPIFY_MATCH_WINDOW_MS) continue;
      // First event wins per actor: the burst is one action, and its first
      // member carries the same name as the rest.
      if (!byActor.has(event.actor!)) byActor.set(event.actor!, event);
    }
    if (byActor.size > 0) matched.set(row.id, [...byActor.values()]);
  }
  return matched;
}
